// Bhashini playback in the browser, shared by both courses. The Prakrit AudioProvider and the Sanskrit audio layer
// (src/sanskrit/lib/audio.ts) call speakBhashini when the learner picked Bhashini in the preferences and has Premium;
// it fetches /api/tts once per text and voice, keeps the audio for the visit, and answers false on any failure so the
// caller falls back to the device voice. The Sanskrit layer reads the choice from voiceConfig(), which SanskritShell
// keeps up to date.

import type { VoiceEngine, VoiceGender } from '@/lib/client/prefs'

export interface VoiceConfig {
    engine: VoiceEngine
    gender: VoiceGender
    /** Premium members only; the server checks again. */
    premium: boolean
    /** The learner's reading speed (0.85 is natural). */
    rate: number
}

let config: VoiceConfig = { engine: 'device', gender: 'female', premium: false, rate: 0.85 }

/** Requests that failed for a reason that will not change during the visit (not set up, not Premium). */
let unavailable = false

export const configureVoice = (next: VoiceConfig): void => {
    // Becoming Premium during the visit tries Bhashini again.
    if (next.premium && !config.premium) unavailable = false

    config = next
}

export const voiceConfig = (): VoiceConfig => config

/** Bhashini plays only when chosen and open to this learner. */
export const bhashiniActive = (cfg: VoiceConfig = config): boolean => cfg.engine === 'bhashini' && cfg.premium

const urls = new Map<string, string>()

let current: HTMLAudioElement | null = null

export const stopBhashini = (): void => {
    if (current) {
        current.pause()
        current = null
    }
}

interface SpeakOptions {
    lang: 'sa' | 'hi'
    gender?: VoiceGender
    /** Reading speed; 0.85 plays the recording as Bhashini made it. */
    rate?: number
    onStart?: () => void
    onEnd?: () => void
}

const audioUrl = async (text: string, lang: 'sa' | 'hi', gender: VoiceGender): Promise<string | null> => {
    const key = `${lang}|${gender}|${text}`
    const hit = urls.get(key)

    if (hit) return hit
    if (unavailable) return null

    try {
        const res = await fetch('/api/tts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, lang, gender })
        })

        if (res.status === 402 || res.status === 503) unavailable = true
        if (!res.ok) return null

        const url = URL.createObjectURL(await res.blob())

        urls.set(key, url)

        return url
    }
    catch {
        return null
    }
}

/** Reads `text` with Bhashini. Resolves true when playback started and ended normally, false when it could not play. */
export const speakBhashini = async (text: string, opts: SpeakOptions): Promise<boolean> => {
    const clean = text.normalize('NFC').replace(/\s+/g, ' ').trim()

    if (!clean || typeof window === 'undefined') return false

    const url = await audioUrl(clean.slice(0, 400), opts.lang, opts.gender ?? config.gender)

    if (!url) return false

    stopBhashini()

    const audio = new Audio(url)
    const rate = (opts.rate ?? config.rate) / 0.85

    audio.playbackRate = Math.min(1.4, Math.max(0.55, rate))
    audio.preservesPitch = true
    current = audio

    return new Promise(resolve => {
        audio.onplay = () => opts.onStart?.()
        audio.onended = () => {
            if (current === audio) current = null
            opts.onEnd?.()
            resolve(true)
        }
        audio.onerror = () => {
            if (current === audio) current = null
            opts.onEnd?.()
            resolve(false)
        }
        audio.play().catch(() => {
            opts.onEnd?.()
            resolve(false)
        })
    })
}
